import { API_URL } from "./config";
import * as api from "./api";
import { parseIndirizzi } from "./intestazione";
import { proxyImages, pareHtml } from "./components/EmailDetail";

const MAX_IMG = 8 * 1024 * 1024;
const MAX_TOT = 40 * 1024 * 1024;

const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// "Studio Rossi - 2026-10-21 - Oggetto.pdf" (caratteri non ammessi nei nomi file rimossi)
export function nomeFile(email, ext) {
  const pulisci = (s) => String(s || "").replace(/<[^>]*>/g, "").replace(/[\\/:*?"<>|\n\r\t]+/g, " ").replace(/\s+/g, " ").trim();
  const mittente = pulisci(email.mittente || email.email || "email").slice(0, 60) || "email";
  const [g, m, a] = String(email.data || "").split(".");
  const data = a ? `${a}-${m}-${g}` : new Date().toISOString().slice(0, 10);
  const oggetto = pulisci(email.oggetto).slice(0, 60);
  return `${mittente} - ${data}${oggetto ? ` - ${oggetto}` : ""}.${ext}`;
}

// Corpo con immagini scaricate e incorporate come data: (quelle che non si riescono a scaricare restano come URL)
async function corpoConImmagini(email) {
  const base = email.corpo_html || (pareHtml(email.corpo) ? email.corpo : `<pre style="white-space:pre-wrap;font-family:inherit">${esc(email.corpo)}</pre>`);
  let html = proxyImages(base, email);
  const rx = new RegExp(`${API_URL.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}/(?:img|emails/attachment)\\?[^"')\\s]+`, "g");
  const urls = [...new Set(html.match(rx) || [])];
  let totale = 0;
  const dati = await Promise.all(
    urls.map(async (u) => {
      try {
        const r = await fetch(u);
        if (!r.ok) return null;
        const blob = await r.blob();
        if (!blob.type.startsWith("image/") || blob.size > MAX_IMG || totale + blob.size > MAX_TOT) return null;
        totale += blob.size;
        return await new Promise((ok) => {
          const fr = new FileReader();
          fr.onload = () => ok(fr.result);
          fr.onerror = () => ok(null);
          fr.readAsDataURL(blob);
        });
      } catch (e) {
        console.debug("esporta: immagine non incorporata", u, e);
        return null;
      }
    })
  );
  urls.forEach((u, i) => {
    if (dati[i]) html = html.split(u).join(dati[i]);
  });
  return html;
}

function riga(etichetta, valore) {
  if (!valore) return "";
  const testo = parseIndirizzi(valore).map((d) => (d.nome ? `${d.nome} &lt;${esc(d.email)}&gt;` : esc(d.email))).join(", ");
  return `<div><b>${etichetta}:</b> ${testo}</div>`;
}

// Documento HTML completo: intestazione (oggetto, da, a, cc, data, allegati) + corpo
export function documentoHtml(email, corpo) {
  const da = email.mittente && email.mittente !== email.email ? `${esc(email.mittente)} &lt;${esc(email.email)}&gt;` : esc(email.email || email.mittente);
  const allegati = (email.allegati || []).filter((x) => !x.inline).map((x) => esc(x.nome)).join(", ");
  return `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>${esc(email.oggetto || "(senza oggetto)")}</title>
<style>
body{margin:0;padding:24px;font-family:Arial,Helvetica,sans-serif;color:#111;background:#fff;-webkit-text-size-adjust:100%}
.intestazione{border-bottom:2px solid #d8dee8;padding-bottom:14px;margin-bottom:20px;font-size:13px;line-height:1.6;color:#334}
.intestazione h1{font-size:20px;margin:0 0 8px;color:#111}
.corpo{font-size:15px;line-height:1.5}
.corpo img{max-width:100%;height:auto}
.corpo table{max-width:100%}
</style></head><body>
<div class="intestazione"><h1>${esc(email.oggetto || "(senza oggetto)")}</h1>
<div><b>Da:</b> ${da}</div>${riga("A", email.a)}${riga("CC", email.cc)}${riga("CCN", email.ccn)}
<div><b>Data:</b> ${esc(email.data)} ${esc(email.ora)}</div>${allegati ? `<div><b>Allegati:</b> ${allegati}</div>` : ""}</div>
<div class="corpo">${corpo}</div></body></html>`;
}

async function emailCompleta(email) {
  if (email.corpo_html !== undefined && !email.light) return email;
  const full = await api.getEmailDetail(email.id);
  return { ...email, ...full };
}

function scaricaBlob(blob, nome) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export async function salvaHtml(email) {
  const em = await emailCompleta(email);
  const doc = documentoHtml(em, await corpoConImmagini(em));
  scaricaBlob(new Blob([doc], { type: "text/html;charset=utf-8" }), nomeFile(em, "html"));
}

// PDF generato nel browser: il documento HTML viene reso in un iframe isolato (stessa resa del visualizzatore,
// senza gli stili dell'app), fotografato e impaginato in A4; poi scaricato direttamente
export async function salvaPdf(email) {
  const em = await emailCompleta(email);
  const doc = documentoHtml(em, await corpoConImmagini(em));
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const LARG = 794; // A4 a 96 dpi
  const fr = document.createElement("iframe");
  fr.setAttribute("aria-hidden", "true");
  fr.style.cssText = `position:absolute;left:-20000px;top:0;width:${LARG}px;height:1200px;border:0;background:#fff;`;
  document.body.appendChild(fr);
  try {
    await new Promise((ok) => {
      fr.onload = ok;
      fr.srcdoc = doc;
    });
    const d = fr.contentDocument;
    await Promise.all([...d.images].map((img) => (img.complete ? null : new Promise((ok) => { img.onload = img.onerror = ok; }))));
    await new Promise((ok) => setTimeout(ok, 80));
    fr.style.height = "10px";
    const altezza = d.documentElement.scrollHeight;
    fr.style.height = altezza + "px";
    const canvas = await html2canvas(d.body, { scale: 2, useCORS: true, logging: false, backgroundColor: "#fff", windowWidth: LARG, width: LARG, height: altezza, windowHeight: altezza, scrollX: 0, scrollY: 0 });
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pagW = pdf.internal.pageSize.getWidth();
    const pagH = pdf.internal.pageSize.getHeight();
    const margine = 8;
    const utileW = pagW - margine * 2;
    const utileH = pagH - margine * 2;
    const mmPerPx = utileW / canvas.width;
    const altPagPx = Math.floor(utileH / mmPerPx);
    const fetta = document.createElement("canvas");
    fetta.width = canvas.width;
    const ctx = fetta.getContext("2d");
    for (let y = 0, pagina = 0; y < canvas.height; y += altPagPx, pagina++) {
      const h = Math.min(altPagPx, canvas.height - y);
      fetta.height = h;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, fetta.width, h);
      ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
      if (pagina > 0) pdf.addPage();
      pdf.addImage(fetta.toDataURL("image/jpeg", 0.92), "JPEG", margine, margine, utileW, h * mmPerPx);
    }
    pdf.save(nomeFile(em, "pdf"));
  } finally {
    fr.remove();
  }
}
